/**
 * Cross-Sector Referral Types — practice-web
 *
 * Mirrors mobile/src/services/referralService.ts types.
 * Supabase-Tabellen: referrals, referral_slots, referral_messages,
 * referral_status_history, referral_consents, practice_referral_settings
 */

// TI-BOUNDARY: Referral-Types definieren den Orchestrierungs-Scope.
// Keine TI/KIM/ePA-spezifischen Types in dieser Datei.
// Siehe: docs/architecture/REFERRAL_COMPLIANCE_BOUNDARY.md

// ─── Enums / Unions ─────────────────────────────────────────

export type ReferralUrgency = "routine" | "soon" | "urgent" | "emergency";

export type ReferralStatus =
    | "draft"
    | "pending_consent"
    | "pending"
    | "sent"
    | "booked"
    | "confirmed"
    | "in_treatment"
    | "completed"
    | "cancelled"
    | "expired";

export type { MedicalSpecialty } from "@/types/medical-specialty";
import type { MedicalSpecialty } from "@/types/medical-specialty";

export type TreatmentOutcome =
    | "successful"
    | "ongoing"
    | "referral_needed"
    | "no_findings"
    | "cancelled";

// ─── Domain Objects ─────────────────────────────────────────

export interface Referral {
    id: string;
    referralNumber: string | null;
    sourcePracticeId: string;
    sourcePracticeName?: string;
    sourceDoctorId: string | null;
    sourceDoctorName?: string;
    targetPracticeId: string | null;
    targetPracticeName?: string;
    targetDoctorId: string | null;
    patientId: string;
    patientName?: string;
    specialty: MedicalSpecialty;
    urgency: ReferralUrgency;
    status: ReferralStatus;
    diagnosisText: string | null;
    reasonForReferral: string | null;
    clinicalQuestion: string | null;
    relevantHistory: string | null;
    attachmentIds: string[];
    specialistNotes: string | null;
    specialistReportText: string | null;
    specialistReportDate: string | null;
    treatmentOutcome: TreatmentOutcome | null;
    reportReadAt: string | null;
    reportReadBy: string | null;
    accessToken: string | null;
    appointmentId: string | null;
    expiresAt: string | null;
    createdAt: string;
    updatedAt: string;
}

export interface ReferralInboxItem {
    id: string;
    referralNumber: string | null;
    patientId: string;
    patientName: string;
    sourcePracticeId: string;
    sourcePracticeName: string;
    targetPracticeId: string;
    specialty: MedicalSpecialty;
    urgency: ReferralUrgency;
    status: ReferralStatus;
    diagnosisText: string | null;
    reasonForReferral: string | null;
    createdAt: string;
}

export interface ReferralMessage {
    id: string;
    referralId: string;
    senderPracticeId: string;
    senderPracticeName?: string;
    messageText: string;
    attachmentIds: string[];
    isRead: boolean;
    createdAt: string;
}

export interface ReferralSlot {
    id: string;
    practiceId: string;
    startTime: string;
    endTime: string;
    isAvailable: boolean;
    doctorId: string | null;
    doctorName?: string;
}

export interface ReferralBooking {
    referralId: string;
    appointmentId: string | null;
    slotId: string;
    bookedAt: string;
}

export type BookSlotResult =
    | { ok: true; booking: ReferralBooking }
    | { ok: false; reason: string };

export interface IncomingReport {
    id: string;
    referralNumber: string | null;
    patientId: string;
    patientName: string;
    targetPracticeId: string;
    targetPracticeName: string;
    targetDoctorName?: string;
    specialty: MedicalSpecialty;
    treatmentOutcome: TreatmentOutcome | null;
    specialistReportText: string | null;
    specialistReportDate: string | null;
    reportReadAt: string | null;
    createdAt: string;
}

export interface ReferralStatusEvent {
    id: string;
    referralId: string;
    previousStatus: ReferralStatus | null;
    newStatus: ReferralStatus;
    changedByUserId: string | null;
    changedByPracticeId: string | null;
    reason: string | null;
    timestamp: string;
}

export interface AvailableSpecialist {
    practiceId: string;
    practiceName: string;
    specialty: MedicalSpecialty;
    address: string | null;
    city: string | null;
    distance: number | null;
    phone: string | null;
    availableSlotCount: number;
    acceptingReferrals: boolean;
    nextAvailableSlot: string | null;
    averageRating: number | null;
    isNetworkPartner: boolean;
}

export interface PracticeReferralSettings {
    practiceId: string;
    acceptsReferrals: boolean;
    maxReferralsPerDay: number | null;
    acceptedSpecialties: MedicalSpecialty[];
    vacationMode: boolean;
    vacationUntil: string | null;
    autoAcceptRoutine: boolean;
    notifyOnNewReferral: boolean;
}

export type NetworkConnectionStatus = 'pending' | 'active' | 'rejected' | 'removed';

export interface NetworkPartner {
    connectionId: string;
    practiceId: string;
    practiceName: string;
    specialty: string | null;
    city: string | null;
    status: NetworkConnectionStatus;
    invitedByUs: boolean;
    createdAt: string;
}

export interface CreateReferralInput {
    sourcePracticeId: string;
    sourceDoctorId?: string;
    patientId: string;
    targetPracticeId?: string;
    specialty: MedicalSpecialty;
    urgency: ReferralUrgency;
    diagnosisText?: string;
    reasonForReferral?: string;
    clinicalQuestion?: string;
    relevantHistory?: string;
    attachmentIds?: string[];
}

export interface SendReportInput {
    referralId: string;
    reportText: string;
    treatmentOutcome: TreatmentOutcome;
    documentIds?: string[];
}

// ─── Display Helpers ────────────────────────────────────────

export { SPECIALTY_LABELS } from "@/types/medical-specialty";

export const URGENCY_LABELS: Record<ReferralUrgency, string> = {
    routine: "Routine",
    soon: "Zeitnah",
    urgent: "Dringend",
    emergency: "Notfall",
};

export const STATUS_LABELS: Record<ReferralStatus, string> = {
    draft: "Entwurf",
    pending_consent: "Einwilligung ausstehend",
    pending: "Ausstehend",
    sent: "Versendet",
    booked: "Gebucht",
    confirmed: "Bestätigt",
    in_treatment: "In Behandlung",
    completed: "Abgeschlossen",
    cancelled: "Storniert",
    expired: "Abgelaufen",
};

export const ACTIONABLE_TARGET_REFERRAL_STATUSES = ["pending", "sent"] as const satisfies readonly ReferralStatus[];

export const ACTIVE_INBOX_REFERRAL_STATUSES = [
    ...ACTIONABLE_TARGET_REFERRAL_STATUSES,
    "booked",
    "confirmed",
    "in_treatment",
] as const satisfies readonly ReferralStatus[];

export const REPORTABLE_TARGET_REFERRAL_STATUSES = [
    "booked",
    "confirmed",
] as const satisfies readonly ReferralStatus[];

export const UNREAD_INCOMING_REFERRAL_STATUSES = [
    ...ACTIONABLE_TARGET_REFERRAL_STATUSES,
] as const satisfies readonly ReferralStatus[];

export const TREATMENT_OUTCOME_LABELS: Record<TreatmentOutcome, string> = {
    successful: "Erfolgreich",
    ongoing: "In Behandlung",
    referral_needed: "Weiterüberweisung",
    no_findings: "Ohne Befund",
    cancelled: "Abgebrochen",
};

export interface StatusDisplay {
    label: string;
    color: string;
    bg: string;
}

export interface UrgencyDisplay {
    label: string;
    color: string;
    bg: string;
    dot: string;
}

const STATUS_DISPLAY: Record<ReferralStatus, StatusDisplay> = {
    draft: { label: "Entwurf", color: "text-neutral-600", bg: "bg-neutral-100" },
    pending_consent: { label: "Einwilligung ausstehend", color: "text-orange-700", bg: "bg-orange-100" },
    pending: { label: "Ausstehend", color: "text-amber-700", bg: "bg-amber-100" },
    sent: { label: "Versendet", color: "text-blue-700", bg: "bg-blue-100" },
    booked: { label: "Gebucht", color: "text-indigo-700", bg: "bg-indigo-100" },
    confirmed: { label: "Bestätigt", color: "text-emerald-700", bg: "bg-emerald-100" },
    in_treatment: { label: "In Behandlung", color: "text-cyan-700", bg: "bg-cyan-100" },
    completed: { label: "Abgeschlossen", color: "text-green-700", bg: "bg-green-100" },
    cancelled: { label: "Storniert", color: "text-red-700", bg: "bg-red-100" },
    expired: { label: "Abgelaufen", color: "text-neutral-500", bg: "bg-neutral-100" },
};

const URGENCY_DISPLAY: Record<ReferralUrgency, UrgencyDisplay> = {
    routine: { label: "Routine", color: "text-green-700", bg: "bg-green-100", dot: "bg-green-500" },
    soon: { label: "Zeitnah", color: "text-amber-700", bg: "bg-amber-100", dot: "bg-amber-500" },
    urgent: { label: "Dringend", color: "text-orange-700", bg: "bg-orange-100", dot: "bg-orange-500" },
    emergency: { label: "Notfall", color: "text-red-700", bg: "bg-red-100", dot: "bg-red-500" },
};

export function getStatusDisplay(status: ReferralStatus): StatusDisplay {
    return STATUS_DISPLAY[status] ?? STATUS_DISPLAY.draft;
}

export function isActionableTargetReferralStatus(status: ReferralStatus): boolean {
    return ACTIONABLE_TARGET_REFERRAL_STATUSES.includes(
        status as (typeof ACTIONABLE_TARGET_REFERRAL_STATUSES)[number],
    );
}

export function canTargetPracticeSendReport(status: ReferralStatus): boolean {
    return REPORTABLE_TARGET_REFERRAL_STATUSES.includes(
        status as (typeof REPORTABLE_TARGET_REFERRAL_STATUSES)[number],
    );
}

export function getUrgencyDisplay(urgency: ReferralUrgency): UrgencyDisplay {
    return URGENCY_DISPLAY[urgency] ?? URGENCY_DISPLAY.routine;
}

// ─── Export-Zone Types (Phase 2) ────────────────────────────

/** Erlaubte Export-Formate. */
export type ReferralExportFormat = "pdf" | "fhir_r4" | "gdt" | "csv";

/** Export-Ziel. */
export type ReferralExportTarget = "download" | "pvs_bridge" | "kim_ready";

/** Zeile in referral_export_events. */
export interface ReferralExportEvent {
    id: string;
    referralId: string;
    exportFormat: ReferralExportFormat;
    exportTarget: ReferralExportTarget;
    deliveryChannel: "kim_queued" | null;
    payloadHash: string;
    exportedAt: string;
    exportedBy: string;
}
